import React, { useState, useEffect } from "react";
import SEOHead from "../section/SeoHead";
import FormsHero from "../forms/FormsHero";
import FormsSearchFilter from "../forms/FormsSearchFilter";
import FormsGrid from "../forms/FormsGrid";
import FormsPagination from "../forms/FormsPagination";
import CategoryHeader from "../forms/CategoryHeader";
import api from "../../utils/api";
import HelpSection from "../section/HelpSection";
import Swal from "sweetalert2";

const Forms = () => {
  const [allForms, setAllForms] = useState([]);
  const [filteredForms, setFilteredForms] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const perPage = 12;

  const fetchAllForms = async () => {
    setLoading(true);
    let allData = [];
    let page = 1;
    let hasMore = true;

    try {
      while (hasMore) {
        const response = await api.get("/forms/active", {
          params: { per_page: 200, page: page },
        });

        const { data } = response.data;

        if (Array.isArray(data) && data.length > 0) {
          allData = [...allData, ...data];
          // If we received fewer items than requested per page (or 100 if server caps it), 
          // we can assume it's the last page. But to be safe, if we get data, we try next page.
          // Better logic: if data.length < request_per_page (e.g. 50? no server forced 100).
          // Let's just create a safe break: if data length is 0, stop.

          // Optimization: If the response doesn't have pagination meta, we rely on data length.
          // If data length < 100 (assuming 100 is the server limit based on behavior), we stop.
          if (data.length < 100) {
            hasMore = false;
          } else {
            page++;
          }
        } else {
          hasMore = false;
        }
      }

      // Remove duplicates just in case
      const uniqueForms = Array.from(new Map(allData.map(item => [item.id, item])).values());

      setAllForms(uniqueForms);
      setFilteredForms(uniqueForms);
    } catch (error) {
      console.error("Error fetching forms:", error);
      Swal.fire("Error", "Failed to load forms.", "error");
      setAllForms([]);
      setFilteredForms([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllForms();
  }, []);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredForms(allForms);
    } else {
      const lowerSearch = searchTerm.toLowerCase();
      const filtered = allForms.filter((form) => {
        return (
          form.name.toLowerCase().includes(lowerSearch) ||
          form.code.toLowerCase().includes(lowerSearch) ||
          (form.description && form.description.toLowerCase().includes(lowerSearch))
        );
      });
      setFilteredForms(filtered);
    }
    setCurrentPage(1);
  }, [searchTerm, allForms]);

  const indexOfLast = currentPage * perPage;
  const indexOfFirst = indexOfLast - perPage;
  const currentForms = filteredForms.slice(indexOfFirst, indexOfLast);

  const totalPages = Math.ceil(filteredForms.length / perPage);

  const paginate = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleClear = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  return (
    <>
      <SEOHead title="Active Forms" description="Browse active immigration forms" />
      <CategoryHeader />
      <FormsHero />

      <FormsSearchFilter
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedCategory="all"
        setSelectedCategory={() => { }}
        categories={[]}
        filteredCount={filteredForms.length}
        totalCount={allForms.length}
        onClear={handleClear}
      />

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading forms...</p>
            </div>
          ) : (
            <>
              <FormsGrid forms={allForms} currentForms={currentForms} />

              {filteredForms.length > 0 && (
                <FormsPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={paginate}
                  totalForms={filteredForms.length}
                  showingStart={indexOfFirst + 1}
                  showingEnd={Math.min(indexOfLast, filteredForms.length)}
                />
              )}
            </>
          )}
        </div>
      </section>

      <HelpSection />
    </>
  );
};

export default Forms;